"use client";

import React, { useState, useEffect } from "react";
import CrmLogin from "@/components/crm/CrmLogin";
import CrmDashboard from "@/components/crm/CrmDashboard";

interface AuthUser {
  id: number;
  username: string;
  role: string;
  fullName: string;
  companyName?: string;
  industry?: string;
  planTier?: string;
  phone?: string;
}

export default function CrmPage() {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [takingLong, setTakingLong] = useState(false);

  const checkAuth = async () => {
    // 3.5s timeout prevents hanging indefinitely on network latency or server cold boot
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3500);

    try {
      const res = await fetch("/api/crm/auth/me", {
        signal: controller.signal,
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.authenticated && data.user) {
          setCurrentUser(data.user);
          setSubscription(data.subscription || null);
          return;
        }
      }
      setCurrentUser(null);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.warn("CRM auth check warning:", err);
      }
      setCurrentUser(null);
    } finally {
      clearTimeout(timeoutId);
      setCheckingAuth(false);
    }
  };

  useEffect(() => {
    checkAuth();

    // Absolute fallback safety timer: never let spinner hang more than 4 seconds
    const safetyTimer = setTimeout(() => {
      setCheckingAuth(false);
    }, 4000);

    // After 1.5 seconds, display an escape link if network is slow
    const slowTimer = setTimeout(() => {
      setTakingLong(true);
    }, 1500);

    return () => {
      clearTimeout(safetyTimer);
      clearTimeout(slowTimer);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await fetch("/api/crm/auth/logout", { method: "POST" });
      setCurrentUser(null);
      setSubscription(null);
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-500 px-4">
        <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
          Verifying CRM Security Session...
        </p>
        {takingLong && (
          <button
            type="button"
            onClick={() => setCheckingAuth(false)}
            className="mt-4 text-xs font-medium text-primary hover:underline transition-all"
          >
            Taking longer than expected? Click here to continue to login &rarr;
          </button>
        )}
      </div>
    );
  }

  if (!currentUser) {
    return <CrmLogin onLoginSuccess={(user) => { setCurrentUser(user); checkAuth(); }} />;
  }

  return (
    <CrmDashboard
      currentUser={currentUser}
      subscription={subscription}
      onSubscriptionUpdate={checkAuth}
      onLogout={handleLogout}
    />
  );
}
